import { createBrowserRouter, Navigate, RouteObject } from "react-router-dom";
import useAuth from "../utils/auth";
import { routeUrls, routeVariables } from "./routeUrls";

import ScreenAccountSetup from "../pages/unprotected/accountSetup/accountSetup";
import ScreenForgotPassword from "../pages/unprotected/forgotPassword/forgotPassword";
import ScreenLogin from "../pages/unprotected/login/login";
import ScreenPrivacyPolicy from "../pages/unprotected/privacy-policy/privacy-policy";
import ScreenResetPassword from "../pages/unprotected/resetPassword/resetPassword";
import ScreenSelect2FAMethod from "../pages/unprotected/select2FAMethod/select2FAMethod";
import ScreenTermsAndConditions from "../pages/unprotected/terms-and-conditions/terms-and-conditions";
import ScreenVerify2FA from "../pages/unprotected/verify2FA/verify2FA";

import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useGetNotificationSummaryQuery } from "../api/network/notificationApiServices";
import svgIcons from "../assets/svg/svgIcons";
import DashboardAdminWrapper from "../components/dashboard/dashboardAdmin";
import DashboardWrapper from "../components/dashboard/dashboardWrapper";
import MapPagesWrapper from "../components/maps/mapPagesWrapper";
import ReportPagesWrapper from "../components/reports/reportPagesWrapper";
import SysadminWrapper from "../components/sysadmin/sysadminWrapper";
import { APP_CONFIG } from "../constants/constants";
import ScreenAdminDetailAccount from "../pages/admin-details/account/account-admin-details";
import ScreenAdminDetailDocument from "../pages/admin-details/documents/document-admin-details";
import ScreenAdminDetailDriver from "../pages/admin-details/driver/driver-admin-details";
import ScreenSysadminDetailEnterprise from "../pages/admin-details/enterprise/enterprise-admin-details";
import ScreenAdminDetailFleettag from "../pages/admin-details/fleettag/fleettag-admin-details";
import ScreenAdminDetailGeozoneCategory from "../pages/admin-details/geozone-category/geozone-category-admin-details";
import ScreenAdminDetailGeozone from "../pages/admin-details/geozone/geozone-admin-details";
import ScreenAdminDetailGroup from "../pages/admin-details/group/group-admin-details";
import ScreenAdminDetailMaintenance from "../pages/admin-details/maintenance/maintenance-admin-details";
import ScreenManageRuleAlertDetails from "../pages/admin-details/manage-rule-alert/rule-alert-admin-details";
import ScreenAdminDetailRole from "../pages/admin-details/role/role-admin-details";
import ScreenAdminDetailUser from "../pages/admin-details/user/user-admin-details";
import ScreenAdminDetailVehicle from "../pages/admin-details/vehicle/vehicle-admin-details";
import ScreenDashboardAdminDocuments from "../pages/admins/documents/documents";
import ScreenDashboardAdminDrivers from "../pages/admins/drivers/drivers";
import AccountUsers from "../pages/admins/enterprises/account-users";
import ScreenSysadminEnterprises from "../pages/admins/enterprises/enterprises";
import ScreenDashboardAdminFleetTags from "../pages/admins/fleettags/fleettags";
import ScreenDashboardAdminGeozoneCategories from "../pages/admins/geozone-categories/geozone-categories";
import ScreenDashboardAdminGeozones from "../pages/admins/geozones/geozones";
import ScreenDashboardAdminGroups from "../pages/admins/groups/groups";
import ScreenAdminHeatmap from "../pages/admins/heatmap/heatmap-admin";
import ScreenAdminEventColors from "../pages/admins/eventColors/eventColorsAdmin";
import CustomIconsListing from "../pages/admins/import-export/customIconListing";
import ScreenAdminImportExport from "../pages/admins/import-export/importExport";
import StandardIconsListing from "../pages/admins/import-export/standardIconListing";
import ScreenDashboardAdminMaintenance from "../pages/admins/maintenance/maintenance";
import ScreenManageRuleAlert from "../pages/admins/manage-rule-alert/manage-rule-alert";
import ScreenDashboardAdminRoles from "../pages/admins/roles/roles";
import ScreenDashboardAdminUsers from "../pages/admins/users/users";
import ScreenDashboardAdminVehicles from "../pages/admins/vehicles/vehicles";
import ScreenBuzzerAlerts from "../pages/buzzer-alert/buzzer-alert";
import ScreenCameraAdmin from "../pages/camera-admin/camera-admin";
import ScreenAIEventDetction from "../pages/camera-admin/configuration/ai-event-detection/ai-event-detection";
import ScreenCameraSettings from "../pages/camera-admin/configuration/camera-settings/camera-settings";
import ScreenSensorEventDetection from "../pages/camera-admin/configuration/sensor-event-detection/sensor-event-detection";
import ScreenTrafficSignAndSignalDetection from "../pages/camera-admin/configuration/traffic-signs-and-signal-detection/traffic-signs-and-signal-detection";
import { CameraAdminContextProvider } from "../pages/camera-admin/context/cameraAdmin.context";
import ScreenCameraDashboard from "../pages/camera-admin/dashboard/cameraDashboard";
import ScreenCameraManagement from "../pages/camera-admin/management/camera-management";
import ScreenCameraManagementDetails from "../pages/camera-admin/management/camera-management-details";
import ScreenCameraVideoHistoryEventDetails from "../pages/camera-admin/video-history/video-history-event-details";
import ScreenCameraVideoHistoryEvents from "../pages/camera-admin/video-history/video-history-events";
import ScreenVideoHistoryFileRetrieval from "../pages/camera-admin/video-history/video-history-file-retrieval";
import ScreenVideoHistoryFileRetrievalDetails from "../pages/camera-admin/video-history/video-history-file-retrieval-details";
import ScreenVideoPlayback from "../pages/camera-admin/video-history/video-playback/video_playback";
import ScreenChangePassword from "../pages/changePassword/changePassword";
import ScreenChat from "../pages/chat/chat";
import ScreenComplianceAdmin from "../pages/compliance-admin/compliance-admin";
import { CrashtrackContextProvider } from "../pages/crash-track/context/crash-track.context";
import ScreenDashboardAdminCrashTrack from "../pages/crash-track/crash-track";
import ScreenAdminDetailCrashTrack from "../pages/crash-track/crash-track-admin-details";
import HelpFeatures from "../pages/help/components/helpFeatures";
import ScreenHelp from "../pages/help/help";
import ScreenManagedAccountAdminDetails from "../pages/managed-account/managed-account-admin-details";
import ScreenManagedAccountUsers from "../pages/managed-account/managed-account-users";
import ScreenMapAsset from "../pages/map-asset/map-asset";
import ScreenMapFleet from "../pages/map-fleet/map-fleet";
import { HeatmapContextProvider } from "../pages/map-heat/context/heatmap.context";
import ScreenMapHeat from "../pages/map-heat/map-heat";
import ScreenMapMultiVehicle from "../pages/map-multi-vehicle/map-multi-vehicle";
import { OverviewContextProvider } from "../pages/overview/context/overview.context";
import ScreenOverview from "../pages/overview/overview";
import { CreateCustomReportContextProvider } from "../pages/reports/context/createCustomReport.context";
import ScreenReportManageSharing from "../pages/reports/manageSharing/manageSharing";
import ScreenSelectAReportType from "../pages/reports/selectAReport/selectAReportType";
import ScreenSingleReport from "../pages/reports/singleReport";
import ScreenAuthAzureCallback from "../pages/unprotected/authAzureCallback/authAzureCallback";
import ScreenAuthenticate from "../pages/unprotected/authenticate/authenticate";
import ScreenAzureLogoutCallback from "../pages/unprotected/azureLogoutCallback/azureLogoutCallback";
import ScreenFourZeroFour from "../pages/unprotected/fourZeroFour/fourZeroFour";
import ScreenVehicleAlerts from "../pages/vehicle-alert/vehicle-alert";
import ScreenReportAlertDetails from "../pages/vehicle-alert/vehicle-alert-details";
import ScreenWhiteLabelSettings from "../pages/white-label-settings/white-label-settings";
import WhiteLabelWrapper from "../pages/white-label-settings/white-label-wrapper";
import ScreenWhiteLabeling from "../pages/white-label-settings/white-labeling";
import { logoutEverywhere } from "../services/authLogoutService";
import { localStorageKeys, useLocalStorage } from "../utils/localStorageItems";
import { useLoggedInUserPerms } from "../utils/user";
import RootLayout from "./rootLayout";
import ScreenAdminIncidentReview from "../pages/admins/incident/incident-review-admin";
import ScreenIncidentListing from "../pages/admins/incident/incident-listing";
import ScreenIncidentDetail from "../pages/admins/incident/incident-detail";
import ScreenAdminInCabCoaching from "../pages/admins/in-cab-coaching/in-cab-control";

const useSessionExpiredPopupHandler = () => {
  const { getLocalStorageItem, removeLocalStorageItem } = useLocalStorage();
  const checkSessionGotExpired = () => {
    const sessionGotExpired = getLocalStorageItem(
      localStorageKeys.sessionGotExpired
    );
    if (!!sessionGotExpired)
      removeLocalStorageItem(localStorageKeys.sessionGotExpired);
    return !!sessionGotExpired;
  };
  return { checkSessionGotExpired };
};

const UnProtectedRoute = ({ element }: { element: JSX.Element }) => {
  const { t } = useTranslation();
  const { isUserLoggedIn } = useAuth();
  const { checkSessionGotExpired } = useSessionExpiredPopupHandler();
  const isLoggedIn = isUserLoggedIn();
  const { loggedInUserIsSysAdmin } = useLoggedInUserPerms();
  if (APP_CONFIG.DEBUG.ALL)
    console.log("[UnProtectedRoute] check isUserLoggedIn", isLoggedIn);
  if (!isLoggedIn) {
    // console.log("===== C - isLoggedIn false");
    const sessionGotExpired = checkSessionGotExpired();
    if (sessionGotExpired) {
      toast.error(t("loginScreen.toast.session_expired"));
    }
    return element;
  } else {
    if (APP_CONFIG.DEBUG.ALL)
      console.log("[UnProtectedRoute] redirect to dashboard");
    return (
      <Navigate
        to={loggedInUserIsSysAdmin() ? routeUrls.sysadmin : routeUrls.dashboard}
      />
    );
  }
};

const ProtectedRoute = ({ element }: { element: JSX.Element }) => {
  const { t } = useTranslation();
  const { isUserLoggedIn } = useAuth();
  const { checkSessionGotExpired } = useSessionExpiredPopupHandler();
  const isLoggedIn = isUserLoggedIn();
  if (APP_CONFIG.DEBUG.ALL)
    console.log("[ProtectedRoute] check isUserLoggedIn", isLoggedIn);

  // prior to accessing protected route, this API call helps validate token
  // and also helps prevent cascaded 401 errors on accessing the app after a long time
  const {
    data: dataNotificationSummary,
    error: errorNotificationSummary,
    isFetching: isFetchingNotificationSummary,
    isError: isErrorNotificationSummary,
  } = useGetNotificationSummaryQuery({ tokenCheck: true });

  if (isFetchingNotificationSummary)
    return (
      <div
        className={`fixed inset-0 flex items-center justify-center bg-white z-map-loading-overlay`}
      >
        <img
          src={svgIcons.LoadingAnimation}
          alt="Loading animation"
          className={`size-16`}
        />
      </div>
    );

  // check if notification summary API results in error
  if (!!isErrorNotificationSummary) {
    logoutEverywhere({ redirect: true, cause: "session-expire" });
    return <div>{t("loginScreen.toast.session_expired_logging_out")}</div>; // <Navigate to={routeUrls.landingPage} />;
  }

  if (isLoggedIn) {
    // console.log("===== A - isLoggedIn true");
    return element;
  } else {
    // console.log("===== B - isLoggedIn false");
    const sessionGotExpired = checkSessionGotExpired();
    return <ScreenLogin showSessionExpiredMessage={sessionGotExpired} />;
  }
};

const TemporaryRoute = () => {
  return (
    <p
      className={`py-24 text-3xl font-bold text-center text-gray-300 ${APP_CONFIG.DES.DASH.P_HORIZ}`}
    >
      This page is under development
    </p>
  );
};

const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    children: [
      // --------------------------
      // --- Unprotected routes ---
      // --------------------------
      {
        path: routeUrls.landingPage,
        element: <UnProtectedRoute element={<ScreenLogin />} />,
      },
      {
        path: routeUrls.landingPageToSetCustomEnv,
        element: <UnProtectedRoute element={<ScreenLogin />} />,
      },
      {
        path: routeUrls.loginPage,
        element: <UnProtectedRoute element={<ScreenLogin />} />,
      },
      {
        path: routeUrls.forgotPassword,
        element: <UnProtectedRoute element={<ScreenForgotPassword />} />,
      },
      {
        path: routeUrls.resetPassword,
        element: <UnProtectedRoute element={<ScreenResetPassword />} />,
      },
      {
        path: routeUrls.termsAndConditions,
        element: <UnProtectedRoute element={<ScreenTermsAndConditions />} />,
      },
      {
        path: routeUrls.privacyPolicy,
        element: <UnProtectedRoute element={<ScreenPrivacyPolicy />} />,
      },
      {
        path: routeUrls.select2FAMethod,
        element: <UnProtectedRoute element={<ScreenSelect2FAMethod />} />,
      },
      {
        path: routeUrls.verify2FA + "/:verifyMethod", // email | mobile
        element: <UnProtectedRoute element={<ScreenVerify2FA />} />,
      },
      {
        path: routeUrls.organizationInvitation,
        element: <UnProtectedRoute element={<ScreenAccountSetup />} />,
      },
      {
        path: routeUrls.authenticate,
        element: <UnProtectedRoute element={<ScreenAuthenticate />} />,
      },
      {
        path: routeUrls.authAzureCallback,
        element: <UnProtectedRoute element={<ScreenAuthAzureCallback />} />,
      },
      {
        path: routeUrls.azureLogoutCallback,
        element: <ScreenAzureLogoutCallback />, // it is important to not use <UnProtectedRoute /> here
      },

      // --------------------------
      // ---- Protected routes ----
      // --------------------------
      {
        path: routeUrls.changePassword,
        element: <ProtectedRoute element={<ScreenChangePassword />} />,
      },
      {
        path: routeUrls.dashboard,
        element: <ProtectedRoute element={<DashboardWrapper />} />,
        children: [
          {
            path: routeUrls.dashboard,
            element: <Navigate to={routeUrls.dashboardChildren.overview} />,
          },
          {
            path: routeUrls.dashboardChildren.overview,
            element: (
              <OverviewContextProvider>
                <ScreenOverview />
              </OverviewContextProvider>
            ),
          },
          {
            path: routeUrls.dashboardChildren.maps,
            element: (
              <OverviewContextProvider>
                <MapPagesWrapper />
                {/* wrapped map pages inside overview context to make the group selector work */}
              </OverviewContextProvider>
            ),
            children: [
              {
                path: routeUrls.dashboardChildren.maps,
                element: (
                  <Navigate
                    to={routeUrls.dashboardChildren.mapsChildren.fleet}
                  />
                ),
              },
              {
                path: routeUrls.dashboardChildren.mapsChildren.fleet,
                element: <ScreenMapFleet />,
              },
              {
                path: routeUrls.dashboardChildren.mapsChildren.heat,
                element: (
                  <HeatmapContextProvider>
                    <ScreenMapHeat />
                  </HeatmapContextProvider>
                ),
              },
              {
                path: routeUrls.dashboardChildren.mapsChildren.multiVehicle,
                element: <ScreenMapMultiVehicle />,
              },
              {
                path:
                  routeUrls.dashboardChildren.mapsChildren.vehicle +
                  `/:${routeVariables.vehicleMap.vehicleId}`,
                element: <ScreenMapAsset />,
              },
              {
                path:
                  routeUrls.dashboardChildren.mapsChildren.vehicle +
                  `/:${routeVariables.vehicleMap.vehicleId}/:${routeVariables.vehicleMap.filterTimestampStart}/:${routeVariables.vehicleMap.filterTimestampEnd}`,
                element: <ScreenMapAsset />,
              },
              {
                path:
                  routeUrls.dashboardChildren.mapsChildren.vehicle +
                  `/:${routeVariables.vehicleMap.vehicleId}/:${routeVariables.vehicleMap.filterTimestampStart}/:${routeVariables.vehicleMap.filterTimestampEnd}/:${routeVariables.vehicleMap.buzzerAlertTimestamp}/:${routeVariables.vehicleMap.buzzerAlertId}`,
                element: <ScreenMapAsset />,
              },
            ],
          },
          {
            path: routeUrls.dashboardChildren.reports,
            element: <ReportPagesWrapper />,
            children: [
              {
                path: routeUrls.dashboardChildren.reports,
                element: (
                  <Navigate
                    to={
                      routeUrls.dashboardChildren.reportsChildren.select_a_type
                    }
                  />
                ),
              },
              {
                path: routeUrls.dashboardChildren.reportsChildren.select_a_type,
                element: (
                  <CreateCustomReportContextProvider>
                    <ScreenSelectAReportType />
                  </CreateCustomReportContextProvider>
                ),
              },
              {
                path:
                  routeUrls.dashboardChildren.reportsChildren.reportPrefix +
                  routeUrls.dashboardChildren.reportsChildren
                    .reportSuffixParamsUri,
                element: (
                  <CreateCustomReportContextProvider>
                    <ScreenSingleReport />
                  </CreateCustomReportContextProvider>
                ),
              },
              {
                path:
                  routeUrls.dashboardChildren.reportsChildren.reportPrefix +
                  routeUrls.dashboardChildren.reportsChildren
                    .reportSuffixParamsUriFiltersOptional,
                element: (
                  <CreateCustomReportContextProvider>
                    <ScreenSingleReport />
                  </CreateCustomReportContextProvider>
                ),
              },
              {
                path:
                  routeUrls.dashboardChildren.reportsChildren.reportPrefix +
                  routeUrls.dashboardChildren.reportsChildren
                    .reportSharingSuffixUri,
                element: <ScreenReportManageSharing />,
              },
            ],
          },
          {
            path: routeUrls.dashboardChildren.incident_review,
            element: <ProtectedRoute element={<ScreenIncidentListing />} />,
          },
          {
            path: routeUrls.dashboardChildren.incident_review_detail + "/:recordid",
            element: <ProtectedRoute element={<ScreenIncidentDetail />} />,
          },
          {
            path: routeUrls.dashboardChildren.manage_roles,
            element: <ScreenDashboardAdminRoles />,
          },
          {
            path: routeUrls.dashboardChildren.maintenance,
            element: <ScreenDashboardAdminMaintenance />,
          },
          {
            path: routeUrls.dashboardChildren.maintenance + "/:deviceId",
            element: <ScreenAdminDetailMaintenance />,
          },
          {
            path: routeUrls.dashboardChildren.admins,
            element: <DashboardAdminWrapper />,
            children: [
              {
                path: routeUrls.dashboardChildren.admins,
                element: (
                  <Navigate
                    to={routeUrls.dashboardChildren.adminChildren.users}
                  />
                ),
              },
              {
                path: routeUrls.dashboardChildren.adminChildren.users,
                element: <ScreenDashboardAdminUsers />,
              },
              {
                path:
                  routeUrls.dashboardChildren.adminChildren.users + "/:userId",
                element: <ScreenAdminDetailUser />,
              },
              {
                path: routeUrls.dashboardChildren.adminChildren.roles,
                element: <ScreenDashboardAdminRoles />,
              },
              {
                path:
                  routeUrls.dashboardChildren.adminChildren.roles + "/:roleId",
                element: <ScreenAdminDetailRole />,
              },
              {
                path: routeUrls.dashboardChildren.adminChildren
                  .manage_rule_alert,
                element: <ScreenManageRuleAlert />,
              },
              {
                path:
                  routeUrls.dashboardChildren.adminChildren.manage_rule_alert +
                  "/:ruleId",
                element: <ScreenManageRuleAlertDetails />,
              },
              {
                path: routeUrls.dashboardChildren.adminChildren.vehicles,
                element: <ScreenDashboardAdminVehicles />,
              },
              {
                path:
                  routeUrls.dashboardChildren.adminChildren.vehicles +
                  "/:vehicleId",
                element: <ScreenAdminDetailVehicle />,
              },
              {
                path: routeUrls.dashboardChildren.adminChildren.groups,
                element: <ScreenDashboardAdminGroups />,
              },
              {
                path:
                  routeUrls.dashboardChildren.adminChildren.groups +
                  "/:groupId",
                element: <ScreenAdminDetailGroup />,
              },
              {
                path: routeUrls.dashboardChildren.adminChildren.fleettags,
                element: <ScreenDashboardAdminFleetTags />,
              },
              {
                path:
                  routeUrls.dashboardChildren.adminChildren.fleettags +
                  "/:fleettagId",
                element: <ScreenAdminDetailFleettag />,
              },
              {
                path: routeUrls.dashboardChildren.adminChildren.drivers,
                element: <ScreenDashboardAdminDrivers />,
              },
              {
                path:
                  routeUrls.dashboardChildren.adminChildren.drivers +
                  "/:driverId",
                element: <ScreenAdminDetailDriver />,
              },
              {
                path: routeUrls.dashboardChildren.adminChildren.geozones,
                element: <ScreenDashboardAdminGeozones />,
              },
              {
                path:
                  routeUrls.dashboardChildren.adminChildren.geozones +
                  "/:geozoneId",
                element: <ScreenAdminDetailGeozone />,
              },
              {
                path: routeUrls.dashboardChildren.adminChildren
                  .geozoneCategories,
                element: <ScreenDashboardAdminGeozoneCategories />,
              },
              {
                path:
                  routeUrls.dashboardChildren.adminChildren.geozoneCategories +
                  "/:geozoneCategoryId",
                element: <ScreenAdminDetailGeozoneCategory />,
              },
              {
                path: routeUrls.dashboardChildren.adminChildren.documents,
                element: <ScreenDashboardAdminDocuments />,
              },
              {
                path:
                  routeUrls.dashboardChildren.adminChildren.documents +
                  "/:documentId",
                element: <ScreenAdminDetailDocument />,
              },
              {
                path: routeUrls.dashboardChildren.adminChildren.importExport,
                element: <ScreenAdminImportExport />,
              },
              {
                path: routeUrls.dashboardChildren.adminChildren.heatmap,
                element: <ScreenAdminHeatmap />,
              },
              {
                path: routeUrls.dashboardChildren.adminChildren.eventColors,
                element: <ScreenAdminEventColors />,
              },
              {
                path: routeUrls.dashboardChildren.adminChildren.incidentReview,
                element: <ProtectedRoute element={<ScreenAdminIncidentReview />} />,
              },
              {
                path: routeUrls.dashboardChildren.adminChildren.inCabCoaching,
                element: <ProtectedRoute element={<ScreenAdminInCabCoaching />} />, //change later
              },
              {
                path: routeUrls.dashboardChildren.adminChildren.standardIcons,
                element: <ProtectedRoute element={<StandardIconsListing />} />,
              },
              {
                path: routeUrls.dashboardChildren.adminChildren.customIcons,
                element: <ProtectedRoute element={<CustomIconsListing />} />,
              },
            ],
          },
          {
            path: routeUrls.dashboardChildren.vehicle_alert,
            element: <ScreenVehicleAlerts />,
          },
          {
            path: routeUrls.dashboardChildren.vehicle_alert + "/:vehicleId",
            element: <ScreenReportAlertDetails />,
          },
          {
            path: routeUrls.dashboardChildren.panic_alert,
            element: <ScreenBuzzerAlerts />,
          },
          {
            path: routeUrls.dashboardChildren.camera_admin,
            element: (
              <CameraAdminContextProvider>
                <ScreenCameraAdmin />
              </CameraAdminContextProvider>
            ),
            children: [
              {
                path: routeUrls.dashboardChildren.camera_admin,
                element: (
                  <Navigate
                    to={routeUrls.dashboardChildren.cameraChildren.dashboard}
                  />
                ),
              },
              {
                path: routeUrls.dashboardChildren.cameraChildren.dashboard,
                element: <ScreenCameraDashboard />,
              },
              {
                path: routeUrls.dashboardChildren.cameraChildren.events,
                element: <ScreenCameraVideoHistoryEvents />,
              },
              {
                path:
                  routeUrls.dashboardChildren.cameraChildren.events +
                  `/:${routeVariables.camera.deviceEventId}`,
                element: <ScreenCameraVideoHistoryEventDetails />,
              },
              {
                path: routeUrls.dashboardChildren.cameraChildren.recordings,
                element: <ScreenVideoHistoryFileRetrieval />,
              },
              {
                path:
                  routeUrls.dashboardChildren.cameraChildren.recordings +
                  "/:ticketId",
                element: <ScreenVideoHistoryFileRetrievalDetails />,
              },
              {
                path: routeUrls.dashboardChildren.cameraChildren
                  .camera_settings,
                element: <ScreenCameraSettings />,
              },
              {
                path: routeUrls.dashboardChildren.cameraChildren
                  .camera_management,
                element: <ScreenCameraManagement />,
              },
              {
                path:
                  routeUrls.dashboardChildren.cameraChildren.camera_management +
                  "/:cameraIdAssetId",
                element: <ScreenCameraManagementDetails />,
              },
              {
                path: routeUrls.dashboardChildren.cameraChildren
                  .camera_settings,
                element: <ScreenCameraSettings />,
              },
              {
                path:
                  routeUrls.dashboardChildren.cameraChildren.camera_settings +
                  "/:cameraId",
                element: <ScreenCameraSettings />,
              },
              {
                path: routeUrls.dashboardChildren.cameraChildren
                  .sensor_event_detection,
                element: <ScreenSensorEventDetection />,
              },
              {
                path:
                  routeUrls.dashboardChildren.cameraChildren
                    .sensor_event_detection + "/:cameraId",
                element: <ScreenSensorEventDetection />,
              },
              {
                path: routeUrls.dashboardChildren.cameraChildren
                  .ai_event_detection_adas,
                element: <ScreenAIEventDetction />,
              },
              {
                path:
                  routeUrls.dashboardChildren.cameraChildren
                    .ai_event_detection_adas + "/:cameraId",
                element: <ScreenAIEventDetction />,
              },
              {
                path: routeUrls.dashboardChildren.cameraChildren
                  .ai_event_detection_dms,
                element: <ScreenAIEventDetction />,
              },
              {
                path:
                  routeUrls.dashboardChildren.cameraChildren
                    .ai_event_detection_dms + "/:cameraId",
                element: <ScreenAIEventDetction />,
              },
              {
                path: routeUrls.dashboardChildren.cameraChildren
                  .traffic_signal_detection,
                element: <ScreenTrafficSignAndSignalDetection />,
              },
              {
                path:
                  routeUrls.dashboardChildren.cameraChildren
                    .traffic_signal_detection + "/:cameraId",
                element: <ScreenTrafficSignAndSignalDetection />,
              },
              {
                path: routeUrls.dashboardChildren.cameraChildren.video_playback,
                element: <ScreenVideoPlayback />,
              },
            ],
          },
          {
            path: routeUrls.dashboardChildren.compliance_admin,
            element: <ScreenComplianceAdmin />,
          },
          {
            path: routeUrls.dashboardChildren.account,
            element: <ScreenAdminDetailAccount />,
          },
          {
            path: routeUrls.dashboardChildren.help,
            element: <ScreenHelp />,
          },
          {
            path: routeUrls.dashboardChildren.help + "/:slug",
            element: <HelpFeatures />,
          },
          {
            path: routeUrls.dashboardChildren.chat,
            element: <ScreenChat />,
          },
          {
            path: routeUrls.dashboardChildren.crashTrack,
            element: (
              <CrashtrackContextProvider>
                <ScreenDashboardAdminCrashTrack />
              </CrashtrackContextProvider>
            ),
          },
          {
            path: routeUrls.dashboardChildren.crashTrack + "/:assetCrashId",
            element: (
              <CrashtrackContextProvider>
                <ScreenAdminDetailCrashTrack />
              </CrashtrackContextProvider>
            ),
          },
          {
            path: routeUrls.dashboardChildren.whiteLabel,
            element: <WhiteLabelWrapper />,
            children: [
              {
                path: routeUrls.dashboardChildren.whiteLabel,
                element: <ScreenWhiteLabeling />,
              },
              {
                path:
                  routeUrls.dashboardChildren.whiteLabelChildren
                    .whitelabel_settings + "/:domainId",
                element: <ScreenWhiteLabelSettings />,
              },
              {
                path: routeUrls.dashboardChildren.whiteLabelChildren.add_new,
                element: <ScreenWhiteLabelSettings />,
              },
            ],
          },
          // ---------------------------------------------------------
          // Managed Accounts
          // ---------------------------------------------------------
          {
            path: routeUrls.dashboardChildren.managedAccounts,
            element: <ScreenManagedAccountAdminDetails />,
          },
          {
            path: routeUrls.dashboardChildren.managedAccountUsers,
            element: <ScreenManagedAccountUsers />,
          },
        ],
      },
      {
        path: routeUrls.sysadmin,
        element: <ProtectedRoute element={<SysadminWrapper />} />,
        children: [
          {
            path: routeUrls.sysadmin,
            element: <Navigate to={routeUrls.sysadminChildren.enterprises} />,
          },
          {
            path: routeUrls.sysadminChildren.enterprises,
            element: <ScreenSysadminEnterprises />,
          },
          {
            path: routeUrls.sysadminChildren.account_users,
            element: <AccountUsers />,
          },
          {
            path: routeUrls.sysadminChildren.enterprises + "/:enterpriseId",
            element: <ScreenSysadminDetailEnterprise />,
          },
        ],
      },
      {
        path: "*",
        element: <ScreenFourZeroFour />,
      },
    ],
  },
];

const router = createBrowserRouter(routes, {
  future: {
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_relativeSplatPath: true,
    v7_skipActionErrorRevalidation: true,
  },
});

// To get all the available routes without slug or any other parameters
const flattenRoutes = (routes: RouteObject[]): string[] => {
  return routes.flatMap((route) => {
    const paths: string[] = [];
    if (route.path && !route.path.includes(":")) {
      paths.push(route.path);
    }
    if (route.children) {
      paths.push(...flattenRoutes(route.children));
    }
    return paths;
  });
};

export const staticRouterPaths = flattenRoutes(routes);

export default router;
